import { Box, Flex, Input, Skeleton, Text } from '@pancakeswap/uikit'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import useSWR from 'swr'
import { formatEther } from 'viem'
import { V4_API_URL, V4_ENABLED } from 'views/TradingCompetitionV4/config'
import { useV4Settings } from 'views/TradingCompetitionV4/hooks'
import CompetitionCard, { CompetitionRow } from './components/CompetitionCard'
import Shell, { Panel } from './components/Shell'
import { useYourClaims } from './components/YourClaims'
import { FAQ, HOW_TO } from './content'
import { TYPES, V3_API_URL, fetcher } from './config'
import { useContractBinding } from './contract'
import { CREATE, REFERRAL } from './routes'
import { useSymbolMap, useV3Settings } from './hooks'
import { Chip, Card, Display, Fluent, Grid, Num, Pill, PrimaryCta } from './ui'

/*
 * 2026-10-06 (Bez): the hero (headline, paragraph, chart art, stat tiles, "Your competitions" box) pushed the list below the fold. It is now one slim
 * page header: trophy, title, the fee / DEX / winner facts as chips, "N to claim" (only when there is something to claim) and Create. The search and
 * filter bar under it sticks right below the site's top nav. First-time visitors get one dismissible "New here? How it works" line.
 */

/* ---------- header ---------- */
const Head = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  padding: 12px 14px;
  background: radial-gradient(70% 190% at 0% 0%, rgba(33, 0, 255, 0.36) 0%, rgba(126, 0, 254, 0.16) 46%, transparent 76%),
    radial-gradient(34% 150% at 100% 100%, rgba(127, 212, 232, 0.12), transparent 72%), ${({ theme }) => theme.colors.backgroundAlt};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 12px 32px rgba(0, 0, 0, 0.35);
  display: grid;
  align-items: center;
  column-gap: 12px;
  /* phones: trophy + title / facts / actions / the "new here" line; each row below the first keeps its own top margin, so a row that is not there
     (the "new here" line once dismissed) leaves no gap behind */
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-areas:
    'icon title'
    'facts facts'
    'actions actions'
    'new new';
  & > [data-area='facts'],
  & > [data-area='actions'],
  & > [data-area='new'] {
    margin-top: 10px;
  }
  /* from 680 px the "new here" line shares the actions row (left) */
  @media screen and (min-width: 680px) {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'icon title title'
      'facts facts facts'
      'new new actions';
  }
  /* wider: the trophy spans both rows; title + facts on the first, "new here" on the left and the actions on the right of the second. The second
     row is always as tall as the Create button, so "N to claim" and the "new here" line never change the header's height. */
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 12px 18px 12px 14px;
    column-gap: 14px;
    row-gap: 6px;
    grid-template-columns: auto auto minmax(0, 1fr) auto;
    grid-template-areas:
      'icon title facts facts'
      'icon new new actions';
    & > [data-area='facts'],
    & > [data-area='actions'],
    & > [data-area='new'] {
      margin-top: 0;
    }
  }
`
const bob = keyframes`0%, 100% { transform: translateY(0) rotate(-4deg); } 50% { transform: translateY(-3px) rotate(4deg); }`
/** the 3D trophy on a soft gold glow */
const Badge = styled.span`
  grid-area: icon;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: radial-gradient(circle at 50% 60%, rgba(255, 194, 74, 0.34), rgba(255, 194, 74, 0) 70%), rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 194, 74, 0.3);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 6px 18px rgba(255, 170, 40, 0.18);
  img {
    filter: drop-shadow(0 4px 8px rgba(255, 170, 40, 0.45));
    animation: ${bob} 3.2s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    img {
      animation: none;
    }
  }
  ${({ theme }) => theme.mediaQueries.md} {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    align-self: center;
    grid-row: 1 / span 2;
  }
`
const Facts = styled.div`
  grid-area: facts;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`
const Fact = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 12px 0 8px;
  border-radius: 999px;
  white-space: nowrap;
  font-size: 13px;
  line-height: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => (theme.isDark ? 'rgba(5, 7, 19, 0.5)' : theme.colors.input)};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  .k {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`
const Actions = styled.div`
  grid-area: actions;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
`
const CreateCta = styled(PrimaryCta)`
  min-height: 46px;
  padding: 0 18px 0 14px;
  font-size: 16px;
  border-radius: 14px;
  white-space: nowrap;
  .long {
    display: none;
  }
  ${({ theme }) => theme.mediaQueries.sm} {
    .long {
      display: inline;
    }
  }
`
const ClaimChip = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 46px; /* the Create button's twin */
  padding: 0 14px 0 10px;
  border-radius: 999px;
  white-space: nowrap;
  font-weight: 800;
  font-size: 14px;
  text-decoration: none;
  /* theme colours are CSS variables here, so the tints are written out (gold #FFC24A) */
  color: ${({ theme }) => theme.colors.gold};
  background: rgba(255, 194, 74, 0.14);
  border: 1px solid rgba(255, 194, 74, 0.55);
  box-shadow: 0 0 18px rgba(255, 194, 74, 0.16);
  &:hover {
    background: rgba(255, 194, 74, 0.22);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.gold};
    outline-offset: 2px;
  }
`
const NewHere = styled.div`
  grid-area: new;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSubtle};
  a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0 4px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin-left: auto;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: ${({ theme }) => theme.colors.textSubtle};
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    &:hover {
      color: ${({ theme }) => theme.colors.text};
      background: rgba(255, 255, 255, 0.06);
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.primary};
    }
  }
  @media screen and (min-width: 680px) {
    button {
      margin-left: 0;
    }
  }
`
const Plus = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ flex: 'none' }}>
    <path d="M9 3v12M3 9h12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
)

const NEWHERE_KEY = 'cadinu.competitions.newHereDismissed'
/** "New here? How it works" until it is dismissed. Read after mount (the page is static), so a visitor who dismissed it never sees it flash. */
function useNewHere() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    let dismissed = false
    try {
      dismissed = window.localStorage.getItem(NEWHERE_KEY) === '1'
    } catch {
      dismissed = false // storage blocked (private mode, site data off): show it, and a dismissal lasts for this visit
    }
    setShow(!dismissed)
  }, [])
  const dismiss = () => {
    try {
      window.localStorage.setItem(NEWHERE_KEY, '1')
    } catch {
      /* storage blocked: hidden for this visit only */
    }
    setShow(false)
  }
  return { show, dismiss }
}

/* ---------- search and filter bar ---------- */
/** sticks to the bottom edge of the site's top nav; the opaque strip around the bar hides the cards scrolling under it */
const Sticky = styled.div`
  position: sticky;
  top: 57px;
  /* above the cards and their pop-overs (30); the whole page is one layer under the site nav (20) and the profile bar (30) */
  z-index: 35;
  margin: 8px 0 0;
  padding: 8px 0;
  background: ${({ theme }) => theme.colors.background};
`
const BarGrid = styled.div`
  display: grid;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 18px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: linear-gradient(90deg, rgba(33, 0, 255, 0.1), rgba(127, 212, 232, 0.05)), ${({ theme }) => theme.colors.backgroundAlt};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 10px 26px rgba(0, 0, 0, 0.32);
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-areas:
    'search prize sort'
    'chips chips chips';
  ${({ theme }) => theme.mediaQueries.lg} {
    /* the chips take what they need and the search field the rest (never under 180 px); below ~1240 px the chips scroll sideways */
    grid-template-columns: minmax(180px, 1fr) minmax(0, auto) auto auto;
    grid-template-areas: 'search chips prize sort';
  }
`
const SearchWrap = styled.div`
  grid-area: search;
  position: relative;
  min-width: 0;
  img {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
`
const SearchBox = styled(Input)`
  height: 44px;
  border-radius: 14px;
  padding-left: 40px;
  font-size: 16px;
`
const Chips = styled(Flex)`
  grid-area: chips;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`
const BarChip = styled(Chip)`
  flex: none;
  padding: 0 13px 0 10px;
`
const Divider = styled.span`
  flex: none;
  width: 1px;
  height: 24px;
  margin: 0 4px;
  background: ${({ theme }) => theme.colors.cardBorder};
`
/** a select with an icon: icon only on phones (the dot marks a filter that is on), icon + text from 968 px */
const Pick = styled.label<{ $on?: boolean; $area: string }>`
  grid-area: ${({ $area }) => $area};
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 44px;
  border-radius: 14px;
  border: 1px solid ${({ theme, $on }) => ($on ? theme.colors.primary : theme.colors.cardBorder)};
  background: ${({ theme }) => theme.colors.input};
  color: ${({ theme }) => theme.colors.text};
  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
  .ico,
  .caret {
    position: absolute;
    top: 50%;
    pointer-events: none;
    display: inline-flex;
  }
  .ico {
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .caret {
    display: none;
    right: 12px;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  &::after {
    content: '';
    display: ${({ $on }) => ($on ? 'block' : 'none')};
    position: absolute;
    top: 6px;
    right: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
  }
  select {
    appearance: none;
    -webkit-appearance: none;
    width: 48px;
    height: 100%;
    padding: 0;
    border: 0;
    border-radius: inherit;
    background: transparent;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    color: transparent;
  }
  option {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.backgroundAlt};
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    .ico {
      left: 12px;
      transform: translateY(-50%);
    }
    .caret {
      display: inline-flex;
    }
    &::after {
      display: none;
    }
    select {
      width: auto;
      padding: 0 30px 0 38px;
      color: inherit;
    }
  }
`
const SortIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M6 15V4M6 4 3 7M6 4l3 3M14 5v11M14 16l-3-3M14 16l3-3" stroke="currentColor" strokeWidth="1.9" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
const Caret = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
    <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * The site's top nav is fixed and slides away on a scroll down (uikit Menu: throttled 200 ms, then a 0.2 s transition), and its height changes when
 * the phishing banner is closed. The bar's `top` follows the nav's bottom edge frame by frame while that can move, so it is never under the nav and
 * never leaves a gap above itself.
 */
function useStickUnderNav(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    let nav: HTMLElement | undefined
    let raf = 0
    let until = 0
    const findNav = () => {
      if (!nav || !nav.isConnected)
        nav = [...document.querySelectorAll('nav')].find((n) => n.parentElement && getComputedStyle(n.parentElement).position === 'fixed') as HTMLElement | undefined
      return nav
    }
    const place = () => {
      const n = findNav()
      el.style.top = `${n ? Math.max(0, Math.round(n.getBoundingClientRect().bottom)) : 0}px`
      raf = performance.now() < until ? requestAnimationFrame(place) : 0
    }
    const follow = () => {
      until = performance.now() + 700
      if (!raf) raf = requestAnimationFrame(place)
    }
    follow()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(follow) : undefined
    const n = findNav()
    if (ro && n?.parentElement) ro.observe(n.parentElement)
    window.addEventListener('scroll', follow, { passive: true })
    window.addEventListener('resize', follow)
    return () => {
      cancelAnimationFrame(raf)
      ro?.disconnect()
      window.removeEventListener('scroll', follow)
      window.removeEventListener('resize', follow)
    }
  }, [ref])
}

/* ---------- the rest of the page ---------- */
const Steps = styled(Box)`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  ${({ theme }) => theme.mediaQueries.xl} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`
/** the "How it works" jump lands below the sticky bar */
const HowTo = styled(Box)`
  scroll-margin-top: 150px;
  ${({ theme }) => theme.mediaQueries.lg} {
    scroll-margin-top: 100px;
  }
`
const StepNo = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-family: 'Martian Mono', ui-monospace, monospace;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
`
const Faq = styled.details`
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &:first-of-type {
    border-top: 0;
  }
  summary {
    cursor: pointer;
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 56px;
    padding: 8px 0;
    font-weight: 700;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary::after {
    content: '+';
    font-size: 22px;
    color: ${({ theme }) => theme.colors.primary};
  }
  &[open] summary::after {
    content: '\\2212';
  }
  p {
    margin: 0 0 16px;
    color: ${({ theme }) => theme.colors.textSubtle};
    max-width: 760px;
  }
`

const SectionHead = ({ title, count, tone, icon }: { title: string; count: number; tone: 'success' | 'info' | 'muted'; icon: string }) => (
  <Flex alignItems="center" style={{ gap: 12 }} mb="12px" mt="24px">
    <Fluent name={icon} size={32} />
    <Display as="h2" bold fontSize={['22px', null, '26px']}>{title}</Display>
    <Pill $tone={tone}>{count}</Pill>
  </Flex>
)

const FILTERS = [
  ['all', 'All', 'party'],
  ['live', 'Live', 'fire'],
  ['upcoming', 'Upcoming', 'calendar'],
  ['finished', 'Finished', 'trophy'],
] as const
const TYPE_ICON: Record<string, string> = { VOLUME: 'chart', PURCHASE_HOLD: 'gem' }
/** the type toggles in the bar use one word each (the approved design: Volume · Hold); the full name is their tooltip */
const TYPE_CHIP: Record<string, string> = { VOLUME: 'Volume', PURCHASE_HOLD: 'Hold' }
/** Reward-token filter: the four tokens people ask for, everything else is "Other". BNB is WBNB on chain. */
const PRIZES = [
  { key: 'usdt', label: 'USDT', address: '0x55d398326f99059fF775485246999027B3197955' },
  { key: 'bnb', label: 'BNB / WBNB', address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c' },
  { key: 'cbon', label: 'CBON', address: '0x6e64fCF15Be3eB71C3d42AcF44D85bB119b2D98b' },
  { key: 'cadinu', label: 'CADINU', address: '0x76e112203eF59D445452ef7556386dD2DF3Ed914' },
] as const
const prizeKey = (token: string) => PRIZES.find((p) => p.address.toLowerCase() === token.toLowerCase())?.key ?? 'other'
const FINISHED = ['settled', 'refunded', 'awaiting_result', 'refundable']

export default function CompetitionListV3() {
  const { apiUrl } = useContractBinding()
  // one list for both contracts: the current one, and the previous one tagged legacy (same API shape, ids restart per contract)
  const { data: cur, error, isLoading, mutate } = useSWR<{ results: CompetitionRow[] }>(`${V4_ENABLED ? V4_API_URL : apiUrl}/competitions?status=all`, fetcher, { refreshInterval: 30000 })
  const { data: old } = useSWR<{ results: CompetitionRow[] }>(V4_ENABLED ? `${V3_API_URL}/competitions?status=all` : null, fetcher, { refreshInterval: 60000, shouldRetryOnError: false })
  const data = useMemo(() => (cur ? { results: [...cur.results, ...(old?.results ?? []).map((r) => ({ ...r, legacy: true }))] } : cur), [cur, old])
  const cfg = useV3Settings()
  // New competitions are created on V4, so the header shows V4's on-chain fees (V3's 0.01 BNB was shown here until 2026-10-04).
  const v4 = useV4Settings()
  const heroFee = useMemo(() => {
    const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 })
    if (!V4_ENABLED) return cfg.cost !== undefined ? [`${formatEther(cfg.cost)} BNB`] : ['…']
    const { bnb, cbon } = v4.fees
    if (!bnb && !cbon) return ['…']
    const parts = [
      bnb?.enabled ? `${formatEther(bnb.cost)} BNB` : null,
      cbon?.enabled ? `${compact.format(Number(formatEther(cbon.cost)))} CBON` : null,
    ].filter((x): x is string => Boolean(x))
    return parts.length ? parts : ['…']
  }, [cfg.cost, v4.fees])
  const claims = useYourClaims()
  const newHere = useNewHere()
  const stickyRef = useRef<HTMLDivElement>(null)
  useStickUnderNav(stickyRef)
  const [filter, setFilter] = useState<(typeof FILTERS)[number][0]>('all')
  const [type, setType] = useState<'all' | string>('all')
  const [sort, setSort] = useState<'newest' | 'ending'>('newest')
  const [prize, setPrize] = useState<'all' | string>('all')
  const [q, setQ] = useState('')
  const rows = useMemo(() => data?.results ?? [], [data])
  const symbols = useSymbolMap(rows.map((r) => r.projectToken))

  const shown = useMemo(() => {
    const raw = q.trim().toLowerCase()
    const needle = raw.replace(/^#/, '')
    return rows.filter((r) => {
      if (type !== 'all' && r.type !== type) return false
      if (prize !== 'all' && prizeKey(r.rewardToken) !== prize) return false
      if (!needle) return true
      if (raw.startsWith('#')) return String(r.id) === needle // "#12" means the id, nothing else
      const addressLike = needle.startsWith('0x') || needle.length >= 8 // short digits must not match inside hex addresses
      return (
        String(r.id) === needle ||
        (symbols[r.projectToken.toLowerCase()] ?? '').toLowerCase().includes(needle) ||
        (addressLike && [r.creator, r.pool, r.projectToken, r.rewardToken].some((a) => a.toLowerCase().includes(needle)))
      )
    })
  }, [rows, type, prize, q, symbols])
  const order = (list: CompetitionRow[], soonBy: 'endTime' | 'startTime') =>
    [...list].sort((a, b) => Number(Boolean(a.legacy)) - Number(Boolean(b.legacy)) || (sort === 'ending' ? a[soonBy] - b[soonBy] : b.id - a.id))
  const live = order(shown.filter((r) => r.phase === 'live'), 'endTime')
  const upcoming = order(shown.filter((r) => r.phase === 'upcoming'), 'startTime')
  const finished = [...shown.filter((r) => FINISHED.includes(r.phase))].sort((a, b) => Number(Boolean(a.legacy)) - Number(Boolean(b.legacy)) || b.id - a.id)
  const sections = [
    { key: 'live', title: 'Live Competitions', tone: 'success' as const, icon: 'fire', list: live, empty: 'No live competitions right now.' },
    { key: 'upcoming', title: 'Upcoming Competitions', tone: 'info' as const, icon: 'calendar', list: upcoming, empty: 'Nothing scheduled yet.' },
    { key: 'finished', title: 'Finished Competitions', tone: 'muted' as const, icon: 'trophy', list: finished, empty: 'No finished competitions yet.' },
  ].filter((s) => filter === 'all' || filter === s.key)
  const none = !isLoading && !error && rows.length === 0

  return (
    <Shell>
      <Head data-testid="v3-head">
        <Badge data-fit="icon"><Fluent name="trophy" size={44} /></Badge>
        <Display as="h1" bold fontSize={['22px', null, '28px']} lineHeight="1.15" color="text" data-fit="title" style={{ gridArea: 'title', minWidth: 0 }}>
          Trading Competitions
        </Display>
        <Facts data-area="facts">
          <Fact data-fit="fee" title="Creation fee, read from the contract">
            <Fluent name="gift" size={20} />
            <span className="k">Fee</span>
            <Num data-testid="v3-head-fee">{heroFee.join(' · ')}</Num>
          </Fact>
          <Fact data-fit="dex" title="Exchanges a competition can run on">
            <Fluent name="chart" size={20} />
            <span data-testid="v3-head-dex"><Num>{cfg.factoryEnabled.length}</Num> DEXes</span>
          </Fact>
          <Fact data-fit="winners" title="Winners per competition">
            <Fluent name="medal1" size={20} />
            <span data-testid="v3-head-winners"><Num>≤ 50</Num> winners</span>
          </Fact>
        </Facts>
        <Actions data-area="actions">
          {claims.rows.length > 0 && (
            <ClaimChip href={REFERRAL} data-testid="v3-head-claims" data-fit="claims" aria-label={`${claims.rows.length} to claim: open Rewards & referral`}>
              <Fluent name="moneybag" size={22} />
              <span><Num>{claims.rows.length}</Num> to claim</span>
            </ClaimChip>
          )}
          <Link href={CREATE} passHref legacyBehavior>
            <CreateCta data-testid="v3-head-create" data-fit="create" aria-label="Create competition">
              <Plus />
              <span>Create<span className="long"> competition</span></span>
            </CreateCta>
          </Link>
        </Actions>
        {newHere.show && (
          <NewHere data-area="new" data-testid="v3-newhere" data-fit="new">
            <Fluent name="sparkles" size={20} />
            <span>New here?</span>
            <a href="#howToEarn">How it works</a>
            <button type="button" aria-label="Dismiss: New here? How it works" title="Dismiss" onClick={newHere.dismiss}>×</button>
          </NewHere>
        )}
      </Head>

      <Sticky ref={stickyRef} data-testid="v3-toolbar">
        <BarGrid>
          <SearchWrap>
            <Fluent name="magnifier" size={20} />
            <SearchBox value={q} onChange={(e) => setQ(e.target.value)} placeholder="Token, #id or address" aria-label="Search competitions" data-testid="v3-search" />
          </SearchWrap>
          <Chips data-testid="v3-chips">
            <Flex role="tablist" aria-label="Status" style={{ gap: 6, flex: 'none' }}>
              {FILTERS.map(([k, label, icon]) => (
                <BarChip key={k} $on={filter === k} onClick={() => setFilter(k)} role="tab" aria-selected={filter === k}><Fluent name={icon} size={18} />{label}</BarChip>
              ))}
            </Flex>
            <Divider aria-hidden="true" />
            {TYPES.map((t) => (
              <BarChip key={t.key} $on={type === t.key} onClick={() => setType(type === t.key ? 'all' : t.key)} aria-pressed={type === t.key} title={t.label} data-testid={`v3-type-chip-${t.key}`}>
                <Fluent name={TYPE_ICON[t.key] ?? 'bolt'} size={18} />{TYPE_CHIP[t.key] ?? t.short}
              </BarChip>
            ))}
          </Chips>
          <Pick $area="prize" $on={prize !== 'all'} title="Prize token">
            <span className="ico"><Fluent name="coin" size={20} /></span>
            <select value={prize} onChange={(e) => setPrize(e.target.value)} aria-label="Prize token" data-testid="v3-prize-filter">
              <option value="all">Any prize</option>
              {PRIZES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              <option value="other">Other</option>
            </select>
            <span className="caret"><Caret /></span>
          </Pick>
          <Pick $area="sort" $on={sort !== 'newest'} title="Sort">
            <span className="ico"><SortIcon /></span>
            <select value={sort} onChange={(e) => setSort(e.target.value as 'newest' | 'ending')} aria-label="Sort" data-testid="v3-sort">
              <option value="newest">Newest</option>
              <option value="ending">Ending soon</option>
            </select>
            <span className="caret"><Caret /></span>
          </Pick>
        </BarGrid>
      </Sticky>

      {isLoading && (
        <Grid mt="16px">
          {[0, 1, 2].map((i) => <Skeleton key={i} height="260px" />)}
        </Grid>
      )}
      {error && (
        <Panel mt="16px" data-testid="v3-error">
          <Text color="failure" bold>Could not load competitions.</Text>
          <Chip onClick={() => mutate()} style={{ marginTop: 12 }}>Try again</Chip>
        </Panel>
      )}
      {none && (
        <Panel mt="16px" data-testid="v3-empty" style={{ textAlign: 'center' }}>
          <Image src="/images/brand/business.webp" alt="" width={168} height={168} unoptimized style={{ margin: '0 auto', display: 'block', height: 'auto', borderRadius: 28 }} />
          <Display bold fontSize="24px" mt="8px">No competitions yet</Display>
          <Text color="textSubtle" mt="8px" mb="16px">The first prize pool is yours to open. Pick a pool, set the reward and the clock starts at your start time.</Text>
          <Link href={CREATE} passHref legacyBehavior>
            <PrimaryCta>Create the first competition</PrimaryCta>
          </Link>
        </Panel>
      )}
      {!isLoading && !error && rows.length > 0 && sections.map((s) => (
        <Box key={s.key} data-testid={`v3-section-${s.key}`}>
          <SectionHead title={s.title} count={s.list.length} tone={s.tone} icon={s.icon} />
          {s.list.length === 0 ? <Flex alignItems="center" style={{ gap: 10, opacity: 0.85 }}><Fluent name={s.icon} size={28} style={{ filter: 'grayscale(0.6)' }} /><Text color="textSubtle">{q || type !== 'all' || prize !== 'all' ? 'No matches.' : s.empty}</Text></Flex> : (
            <Grid>{s.list.map((c) => <CompetitionCard key={`${c.legacy ? 'l' : 'c'}${c.id}`} c={c} />)}</Grid>
          )}
        </Box>
      ))}

      <HowTo id="howToEarn" mt="56px">
        <Display as="h2" bold fontSize={['26px', null, '32px']} mb="16px">How to Win</Display>
        <Steps>
          {HOW_TO.map((s, i) => (
            <Card key={s.title}>
              <Flex alignItems="center" justifyContent="space-between">
                <StepNo>{i + 1}</StepNo>
                <Fluent name={['key', 'bolt', 'chart', 'gift'][i] ?? 'party'} size={36} />
              </Flex>
              <Display bold fontSize="19px" mt="12px" mb="8px">{s.title}</Display>
              <Text color="textSubtle" fontSize="15px">{s.body}</Text>
            </Card>
          ))}
        </Steps>
      </HowTo>

      <Box mt="56px">
        <Display as="h2" bold fontSize={['26px', null, '32px']} mb="16px">Still Got Questions?</Display>
        <Card>
          {FAQ.map((f) => (
            <Faq key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </Faq>
          ))}
        </Card>
      </Box>
    </Shell>
  )
}
